import React, { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { Modal, ModalTitle, ModalContent, ModalFooter, Button, TextArea, TextInput, Select, SelectOption } from "@utahdts/utah-design-system";
import { GET_STATUS_OPTIONS } from "../../queries/waterlinkQrys";
import { GET_QUESTION_BY_ID } from "../../queries/siteVisitQrys/siteVisitSectionsAndQuestionsQrys";
import { formatDate } from "../../utilities/commonFunctions/formattingFunctions";


const QuestionModal = ({ section, questionSummary, canEdit, onClose }) => {
  const { data: statusData, loading: statusLoading, error: statusError } = useQuery(GET_STATUS_OPTIONS);
  const statusOptions = statusData?.getStatusOptions ?? [];
  const { data: questionData, loading: questionLoading, error: questionError } = useQuery(GET_QUESTION_BY_ID, {
    variables: { questionId: questionSummary?.questionId },
    skip: !questionSummary?.questionId,
  });
  const response = questionData?.getQuestionById ?? questionSummary;
  const isAddQuestion = !questionSummary;
  const [question, setQuestion] = useState({});
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    setQuestion({
      sectionId: response?.sectionId ?? section?.sectionId ?? "",
      questionId: response?.questionId ?? "",
      orderNum: response?.orderNum ?? "",
      leadInOption: response?.leadInOption ?? "",
      leadInQuestionId: response?.leadInQuestionId ?? "",
      question: response?.question ?? "",
      answerType: response?.answerType ?? "",
      contactType: response?.contactType ?? "",
      answerOptions: response?.answerOptions ?? "",
      answerCorrectValues: response?.answerCorrectValues ?? "",
      status: response?.status ?? "",
      deficiency: response?.deficiency ?? "",
      answerSql: response?.answerSql ?? "",
      siteVisitType: response?.siteVisitType ?? "",
      lastUpdatedUser: response?.lastUpdatedUser ?? "",
      lastUpdatedTs: response?.lastUpdatedTs ?? "",
      createdUser: response?.createdUser ?? "",
      createdTs: response?.createdTs ?? ""
    });
  }, [response, section]);

  const updateForm = (field, value) => {
    setQuestion((previousQuestion) => ({ ...previousQuestion, [field]: value }));
    setFormErrors((previousErrors) => ({ ...previousErrors, [field]: false }));
  };

  const handleSave = () => {
    const nextErrors = {
      question: !String(question.question ?? "").trim(),
      answerType: !String(question.answerType ?? "").trim(),
      status: !String(question.status ?? "").trim(),
      contactType: question.answerType === "Contact Information" && !String(question.contactType ?? "").trim(),
      answerOptions: question.answerType === "Drop Down" && !String(question.answerOptions ?? "").trim(),
    };

    setFormErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    onClose();
  };

  if (!section && !questionSummary) return null;

  return (
    <div className="utah-design-system wl-modal">
      <Modal
        className="modal--large"
        onClose={onClose}
        onEscape={onClose}
      >
        <ModalTitle>{isAddQuestion ? "Add Question" : "Edit Question"}</ModalTitle>
        <ModalContent>
          {(statusError || questionError) && (
            <div className="site-visits-save-error" role="alert">
              Could not load question form data: {statusError?.message || questionError?.message}
            </div>
          )}
          <div className="modal-row">
            <strong>Section:</strong> {section?.description}
          </div>
          <div className="modal-row">
            <div className="modal-row-item">
              <TextArea
                id="question-text"
                label="Question Text:"
                value={question.question}
                onChange={(event) => updateForm("question", event.target.value)}
                isDisabled={!canEdit}
                isInvalid={formErrors.question}
                isRequired
              />
            </div>
          </div>
          <div className="modal-row">
            <div className="modal-row-item">
              <Select
                id="question-answer-type"
                label="Answer Type"
                value={question.answerType}
                onChange={(event) => updateForm("answerType", event.target.value)}
                isDisabled={!canEdit}
                isInvalid={formErrors.answerType}
                isRequired
                placeholder="---"
              >
                {/* TODO: Get an actual list of answer types */}
                {response?.answerType && (
                  <SelectOption value={response.answerType} label={response.answerType} />
                )}
              </Select>
            </div>
            
            <div className="modal-row-item">
              {question.answerType === "Contact Information" && (
                <Select
                  id="question-contact-type"
                  label="Contact Type"
                  value={question.contactType}
                  onChange={(event) => updateForm("contactType", event.target.value)}
                  isDisabled={!canEdit}
                  isInvalid={formErrors.contactType}
                  isRequired
                  placeholder="---"
                >
                  {/* TODO: Get an actual list of contact types */}
                  {response?.contactType && (
                    <SelectOption value={response.contactType} label={response.contactType} />
                  )}
                </Select>
              )}
              {question.answerType === "Drop Down" && (
                <Select
                  id="question-answer-options"
                  label="Drop Down:"
                  value={question.answerOptions}
                  onChange={(event) => updateForm("answerOptions", event.target.value)}
                  isDisabled={!canEdit}
                  isInvalid={formErrors.answerOptions}
                  isRequired
                  placeholder="---"
                >
                {/* TODO: Get an actual list of answerOptions*/}
                  {response?.answerOptions && (
                    <SelectOption value={response.answerOptions} label={response.answerOptions} />
                  )}
                </Select>
              )}

              {/* TODO: these should probably show up conditionally based on answer type 
                  find out if answerCorrectValues could be a multiselect or an input based on type?
              */}
            
              <TextInput
                id="question-answer-correct-values"
                label="Correct Answer Values"
                value={question.answerCorrectValues}
                onChange={(event) => updateForm("answerCorrectValues", event.target.value)}
                isDisabled={!canEdit}
              />
              
              <TextInput
                id="question-lead-in-option"
                label="Lead-in Option"
                value={question.leadInOption}
                onChange={(event) => updateForm("leadInOption", event.target.value)}
                isDisabled={!canEdit}
              />
            </div>
          </div>
          {/* TODO: Get an actual list of deficiencies 
            This seems like it should conditionally display too.  find out what those conditions are.
          */}
          <div className="modal-row">
            <div className="modal-row-item">
              
              <Select
                id="question-deficiency"
                label="Deficiency"
                value={question.deficiency}
                onChange={(event) => updateForm("deficiency", event.target.value)}
                isDisabled={!canEdit}
                placeholder="---"
              >
                {response?.deficiency && (
                  <SelectOption value={response.deficiency} label={response.deficiency} />
                )}
              </Select>
            </div>
          </div>
          
          <div className="modal-row">
            <div className="modal-row-item">
              <TextArea
                id="question-answer-sql"
                label="Answer SQL:"
                value={question.answerSql}
                onChange={(event) => updateForm("answerSql", event.target.value)}
                isDisabled={!canEdit}
              />
            </div>
          </div>

          <div className="modal-row">
            <div className="modal-row-item">
              <TextInput
                id="question-order-num"
                label="Order Number"
                value={question.orderNum}
                onChange={(event) => updateForm("orderNum", event.target.value)}
                isDisabled={!canEdit}
              />
            </div>
            <div className="modal-row-item">
              <Select
                id="question-status"
                label="Status"
                value={question.status}
                onChange={(event) => updateForm("status", event.target.value)}
                isDisabled={!canEdit}
                isInvalid={formErrors.status}
                isRequired
                placeholder="---"
              >
                {statusOptions.map((status) => (
                  <SelectOption key={status} value={status} label={status} />
                ))}
              </Select>
            </div>
            <div className="modal-row-item">
              <TextInput
                id="question-site-visit-type"
                label="Site Visit Type"
                value={question.siteVisitType}
                onChange={(event) => updateForm("siteVisitType", event.target.value)}
                isDisabled={!canEdit}
              />
            </div>
          </div>
          {!isAddQuestion && (
            <div className="modal-row">
              <div className="modal-row-item">
                <strong>Last updated: </strong>
                {question.lastUpdatedTs ? (
                  <>
                    {formatDate(question.lastUpdatedTs)}
                    <br />by {question.lastUpdatedUser}
                  </>
                ) : ("—")}
              </div>
              <div className="modal-row-item">
                <strong>Created: </strong>
                {question.createdTs ? (
                  <>
                    {formatDate(question.createdTs)}
                    <br />
                    by {question.createdUser}
                  </>
                ) : ("—")}
              </div>
            </div>
          )}
          {Object.values(formErrors).some(Boolean) && (
            <div className="site-visits-save-error" role="alert">
              Please provide the required fields before saving.
            </div>
          )}
        </ModalContent>
        <ModalFooter>
          <Button onClick={onClose}>Cancel</Button>
          <Button appearance="solid" color="primary" isDisabled={!canEdit || statusLoading || questionLoading} onClick={handleSave}>Save</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default QuestionModal;
